import type { AlertClass, AlertDisposition, VendorId } from '@/types/domain';
import type { HistoricalAlertId, ScenarioId } from '@/types/ids';

/**
 * AlertsApi — typed client for the Live alerts feed screen (BC-05 surface
 * over BC-01 `HistoricalAlert` and BC-04 `ValidationRun` outputs).
 *
 * BRD §17.5 names the canonical `HistoricalAlert` fields; BRD §20 defines
 * the payment-aware parsing rules that populate `supportingTransactions`.
 * The detail drawer renders `rawPayload` verbatim so analysts can inspect
 * the original vendor message before any canonicalisation.
 *
 * Implementations:
 *   - api.mock.ts — MSW handlers + `createMockAlertsApi()` for unit tests.
 *   - api.fetch.ts — `openapi-fetch`-style client hitting `sentinel-api`.
 */

export interface AlertRow {
  alertId: HistoricalAlertId;
  /** Human-readable alert display id (e.g. `ALR-8841`) — design copy. */
  displayId: string;
  /** Scenario (canonical name rendered in the cell). */
  scenarioId: ScenarioId;
  scenarioName: string;
  /** Source platform (vendor). */
  sourcePlatform: VendorId;
  /** Severity class shown as chip. */
  alertClass: AlertClass;
  /** Short description (lifted from vendor payload). */
  headline: string;
  /** ISO-8601 timestamp the upstream engine raised the alert. */
  raisedAt: string;
  /** Canonical disposition. */
  disposition: AlertDisposition;
  /** BRD §17.5.9 — alert_grouping_key (value only; breakdown in detail). */
  groupingKey: string;
}

/** Single supporting transaction emitted by `/alerts/:id/transactions`. */
export interface SupportingTransaction {
  txId: string;
  amount: string;
  currency: string;
  messageType: string;
  direction: 'DEBIT' | 'CREDIT';
  counterparty: string;
}

/**
 * Full detail payload for the `AlertDetail` drawer. Vendor-verbatim body
 * lives in `rawPayload`; all other fields are canonical.
 */
export interface AlertDetail extends AlertRow {
  /** Vendor JSON/XML rendered verbatim in a <pre>. */
  rawPayload: string;
  /** Structured breakdown of the grouping key segments. */
  groupingBreakdown: Array<{ label: string; value: string }>;
  /** Supporting transactions — BRD §17.5.8 plus §20.1 message type. */
  supportingTransactions: SupportingTransaction[];
}

export interface AlertsQuery {
  /** Filter by disposition; `undefined` means "any". */
  disposition?: AlertDisposition;
  /** Filter by scenario id; `undefined` means "any". */
  scenarioId?: ScenarioId;
  /** Filter by source platform. */
  sourcePlatform?: VendorId;
  /** Filter by severity class (LOW/MED/HIGH). */
  alertClass?: AlertClass;
  /** Free-text `contains` match on the alert grouping key. */
  groupingKey?: string;
}

export interface AlertsApi {
  listAlerts(query: AlertsQuery): Promise<AlertRow[]>;
  getAlert(id: HistoricalAlertId): Promise<AlertDetail | null>;
  /** Fetch supporting transactions for one alert (BRD §17.5.8). */
  listTransactions(id: HistoricalAlertId): Promise<SupportingTransaction[]>;
  /**
   * Analyst-driven disposition override. Returns the canonical
   * disposition string on success; the backend stores the reason in the
   * in-memory overlay until the `HistoricalAlertRepo` projection lands.
   */
  patchDisposition(
    id: HistoricalAlertId,
    input: { disposition: AlertDisposition; reason: string },
  ): Promise<{ alertId: string; disposition: AlertDisposition; setAt: string }>;
}

export const ALERTS_ENDPOINTS = {
  list: '/api/v1/alerts',
  byId: (id: string) => `/api/v1/alerts/${id}`,
  transactions: (id: string) => `/api/v1/alerts/${id}/transactions`,
  disposition: (id: string) => `/api/v1/alerts/${id}/disposition`,
} as const;

/** Minimal scenario tile returned by `/api/v1/scenarios` (filter dropdown). */
export interface ScenarioOption {
  scenarioId: string;
  name: string;
}
